import { useEffect, useState } from "react";
import { Download, NotebookPen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, Empty } from "../panel/PanelShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Field, inputCls } from "../forms/Fields";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, fmtDateTime } from "../../lib/format";

const STATUSES = ["new", "contacted", "follow_up", "converted", "closed"];

const COLUMNS = {
  consultations: [["Name", "name"], ["Mobile", "mobile"], ["Email", "email"], ["City", "city"], ["Service", "service"], ["Budget", "budget"], ["Preferred", "preferred_date", fmtDate]],
  callbacks: [["Name", "name"], ["Mobile", "mobile"], ["Preferred time", "preferred_time"]],
  enquiries: [["Name", "name"], ["Email", "email"], ["Mobile", "mobile"], ["Subject", "subject"]],
};

function exportCsv(kind, rows) {
  const cols = [...COLUMNS[kind].map((c) => c[1]), "message", "status", "notes", "follow_up_date", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `quantora-${kind}.csv`;
  a.click();
}

function NotesDialog({ lead, onClose, onSave }) {
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  useEffect(() => { setNotes(lead?.notes || ""); setDate(lead?.follow_up_date || ""); }, [lead]);
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Follow-up · {lead?.name}</DialogTitle></DialogHeader>
        {lead?.message && <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">"{lead.message}"</p>}
        <Field label="Follow-up date" id="fu-date"><input id="fu-date" type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="lead-followup-date-input" /></Field>
        <Field label="Internal notes" id="fu-notes"><Textarea id="fu-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes-input" /></Field>
        <button onClick={() => onSave({ notes, follow_up_date: date })} data-testid="lead-notes-save-btn" className="rounded-full bg-navy py-3 text-sm font-semibold text-white">Save</button>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadsTable({ kind }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/leads/${kind}`, { params: filter === "all" ? {} : { status: filter } }).then((r) => setRows(r.data));
  useEffect(() => { setRows(null); load(); }, [kind, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (id, body) => {
    try {
      await api.patch(`/admin/leads/${kind}/${id}`, body);
      toast.success("Lead updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/admin/leads/${kind}/${id}`);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`leads-filter-${s}`} className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize ${filter === s ? "bg-navy text-white" : "bg-white text-slate-600"}`}>{s.replace("_", " ")}</button>
          ))}
        </div>
        <button onClick={() => exportCsv(kind, rows || [])} data-testid="leads-export-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-navy"><Download size={15} /> Export CSV</button>
      </div>
      {rows && !rows.length && <Empty text="No records found." testid="leads-empty" />}
      {!!rows?.length && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[900px] text-sm" data-testid={`leads-table-${kind}`}>
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>{COLUMNS[kind].map(([h]) => <th key={h} className="px-4 py-3">{h}</th>)}<th className="px-4 py-3">Received</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Follow-up</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`lead-row-${r.id}`}>
                  {COLUMNS[kind].map(([h, k, f]) => <td key={h} className="px-4 py-3 text-slate-700">{f ? f(r[k]) : r[k] || "-"}</td>)}
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <Select value={r.status} onValueChange={(v) => update(r.id, { status: v })}>
                      <SelectTrigger className="h-8 w-32 text-xs capitalize" data-testid={`lead-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`lead-status-option-${s}`}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.follow_up_date ? fmtDate(r.follow_up_date) : "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(r)} data-testid={`lead-notes-${r.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Notes"><NotebookPen size={15} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`lead-delete-${r.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <NotesDialog lead={editing} onClose={() => setEditing(null)} onSave={async (b) => { await update(editing.id, b); setEditing(null); }} />
    </div>
  );
}
